import type { MutableRefObject } from 'react';
import * as React from 'react';
import { css, styled } from 'styled-components';

import { Button, Icon, tk, Typography } from '@sunwebgroup/ui-components';

import { usePagination } from '@/scopes/search/search/domain/hooks';
import { useDebouncedCallback } from '@/scopes/common/components/hooks/useDebouncedCallback';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

interface PaginationProps {
  scrollToElement: MutableRefObject<HTMLDivElement | null>;
}

export function Pagination({ scrollToElement }: PaginationProps) {
  const {
    isFetching,
    isFirstPage,
    isLastPage,
    currentPage,
    paginationRange,
    setNextPage,
    setPreviousPage,
    setPage,
    DOTS,
  } = usePagination();
  const { sendEvent } = useAnalytics();

  const scrollToTop = useDebouncedCallback(() => {
    requestAnimationFrame(() => {
      if (scrollToElement.current) {
        const rect = scrollToElement.current.getBoundingClientRect();

        scrollTo({
          top: rect.top + window.scrollY,
          behavior: 'smooth',
        });
      }
    });
  }, 500);

  const handlePreviousPage = () => {
    sendPaginationSearchEvent(currentPage - 1);
    setPreviousPage();
    scrollToTop();
  };

  const handleNextPage = () => {
    sendPaginationSearchEvent(currentPage + 1);
    setNextPage();
    scrollToTop();
  };

  const handleSetPage = (pageNumber: number | string) => {
    setPage(pageNumber);
    scrollToTop();
  };

  const sendPaginationSearchEvent = (pageNumber: number) => {
    sendEvent({
      event: 'paginationSearch',
      description: 'pagination of product listing',
      element: 'number',
      type: 'result page',
      result: pageNumber,
    });
  };

  return (
    <Wrapper>
      <Button $hierarchy="secondary" $withoutColor onClick={handlePreviousPage} disabled={isFetching || isFirstPage}>
        <Icon glyph="IconChevronLeft" />
      </Button>
      {paginationRange.map((pageNumber: number | string, index: number) => {
        if (pageNumber === DOTS) {
          return (
            <Typography key={index} $variant="label-thin">
              {pageNumber}
            </Typography>
          );
        }

        return (
          <PaginationButton
            $hierarchy="tertiary"
            $withoutColor
            onClick={() => handleSetPage(pageNumber)}
            $selected={pageNumber === currentPage}
            key={index}
          >
            {pageNumber}
          </PaginationButton>
        );
      })}
      <Button $hierarchy="secondary" $withoutColor onClick={handleNextPage} disabled={isFetching || isLastPage}>
        <Icon glyph="IconChevronRight" />
      </Button>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 460px;
  margin: ${tk('spacing_xl')} auto;

  > * + * {
    ${tk('breakpoint_tablet')} {
      margin-left: ${tk('spacing_lg')};
    }
  }

  ${Button} {
    ${Button.modSize('small')}
  }

  @media ${tk('mq_tablet')} {
    ${Button} {
      ${Button.modSize('default')}
    }
  }
`;

const PaginationButton = styled(Button)<{ $selected: boolean }>`
  padding: 0 !important;

  ${({ $selected }) =>
    $selected
      ? css`
          background-color: ${tk('color_semantic_bg_strong')};
        `
      : css`
          @media (hover: hover) {
            &:hover {
              background-color: ${tk('color_semantic_bg_strong')};
            }
          }
        `}
`;
